import { useState } from 'react';
import {
  Container,
  Title,
  Button,
  Group,
  Stack,
  Text,
  Paper,
  TextInput,
  ActionIcon,
  Menu,
  Badge,
  Accordion,
  Modal,
  Table,
  Center,
  Loader,
  rem,
  Box,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
  IconDotsVertical,
  IconBuilding,
  IconDoor,
  IconMapPin,
} from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useSites, useCreateSite, useUpdateSite, useDeleteSite, formatSiteAddress, type SiteFormData } from '@/hooks/api/useSites';
import { useLocations, useCreateLocation, useUpdateLocation, useDeleteLocation, type LocationFormData } from '@/hooks/api/useLocations';
import type { Site, Location } from '@/types/models';

export function LocationsPage() {
  const { organizationId } = useOrganization();
  const { data: sites = [], isLoading: sitesLoading } = useSites(organizationId);
  const { data: locations = [], isLoading: locationsLoading } = useLocations(organizationId);

  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  const deleteLocation = useDeleteLocation();

  const [search, setSearch] = useState('');
  const [siteModalOpened, setSiteModalOpened] = useState(false);
  const [subLocationModalOpened, setSubLocationModalOpened] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  // Form state
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [siteState, setSiteState] = useState('');
  const [sitePostalCode, setSitePostalCode] = useState('');
  const [subLocationName, setSubLocationName] = useState('');

  const isLoading = sitesLoading || locationsLoading;

  // Get sub-locations for a site
  const getSubLocations = (siteId: string) =>
    locations.filter((loc) => loc.site_id === siteId);

  // Filter sites by search
  const filteredSites = sites.filter(
    (site) =>
      search === '' ||
      site.name.toLowerCase().includes(search.toLowerCase()) ||
      formatSiteAddress(site).toLowerCase().includes(search.toLowerCase())
  );

  // Handle site creation/edit
  const handleSiteSubmit = async () => {
    if (!organizationId || !siteName.trim()) return;

    if (editingSite) {
      await updateSite.mutateAsync({
        id: editingSite.id,
        data: {
          name: siteName.trim(),
          address: siteAddress.trim() || undefined,
          city: siteCity.trim() || undefined,
          state: siteState.trim() || undefined,
          postal_code: sitePostalCode.trim() || undefined,
        },
      });
    } else {
      await createSite.mutateAsync({
        name: siteName.trim(),
        address: siteAddress.trim() || undefined,
        city: siteCity.trim() || undefined,
        state: siteState.trim() || undefined,
        postal_code: sitePostalCode.trim() || undefined,
        organization_id: organizationId,
      });
    }

    closeSiteModal();
  };

  // Handle sub-location creation/edit
  const handleSubLocationSubmit = async (keepOpen = false) => {
    if (!organizationId || !subLocationName.trim()) return;

    if (editingLocation) {
      await updateLocation.mutateAsync({
        id: editingLocation.id,
        data: {
          name: subLocationName.trim(),
        },
      });
      closeSubLocationModal();
    } else if (selectedSiteId) {
      await createLocation.mutateAsync({
        name: subLocationName.trim(),
        site_id: selectedSiteId,
        type: 'Room',
        status: 'Active',
        organization_id: organizationId,
      });

      if (keepOpen) {
        // Clear just the name, keep the site selected for adding more
        setSubLocationName('');
      } else {
        closeSubLocationModal();
      }
    }
  };

  // Open site modal for creation
  const openCreateSiteModal = () => {
    setEditingSite(null);
    setSiteName('');
    setSiteAddress('');
    setSiteCity('');
    setSiteState('');
    setSitePostalCode('');
    setSiteModalOpened(true);
  };

  // Open site modal for editing
  const openEditSiteModal = (site: Site) => {
    setEditingSite(site);
    setSiteName(site.name);
    setSiteAddress(site.address || '');
    setSiteCity(site.city || '');
    setSiteState(site.state || '');
    setSitePostalCode(site.postal_code || '');
    setSiteModalOpened(true);
  };

  // Close site modal
  const closeSiteModal = () => {
    setSiteModalOpened(false);
    setEditingSite(null);
    setSiteName('');
    setSiteAddress('');
    setSiteCity('');
    setSiteState('');
    setSitePostalCode('');
  };

  // Open sub-location modal for creation
  const openCreateSubLocationModal = (siteId: string) => {
    setEditingLocation(null);
    setSelectedSiteId(siteId);
    setSubLocationName('');
    setSubLocationModalOpened(true);
  };

  // Open sub-location modal for editing
  const openEditSubLocationModal = (location: Location) => {
    setEditingLocation(location);
    setSelectedSiteId(location.site_id);
    setSubLocationName(location.name);
    setSubLocationModalOpened(true);
  };

  // Close sub-location modal
  const closeSubLocationModal = () => {
    setSubLocationModalOpened(false);
    setEditingLocation(null);
    setSelectedSiteId(null);
    setSubLocationName('');
  };

  // Delete confirmation
  const confirmDeleteSite = (site: Site) => {
    const subLocationCount = getSubLocations(site.id).length;
    modals.openConfirmModal({
      title: 'Delete Primary Location',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{site.name}</strong>?
          {subLocationCount > 0 && (
            <Text c="red" size="sm" mt="xs">
              Warning: This location has {subLocationCount} sub-location(s) that will be unlinked.
            </Text>
          )}
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteSite.mutate(site.id),
    });
  };

  const confirmDeleteSubLocation = (location: Location) => {
    modals.openConfirmModal({
      title: 'Delete Sub-Location',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{location.name}</strong>?
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteLocation.mutate(location.id),
    });
  };

  const selectedSiteForModal = selectedSiteId ? sites.find((s) => s.id === selectedSiteId) : null;

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader />
      </Center>
    );
  }

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Locations</Title>
            <Text c="dimmed" size="sm">
              Manage your primary locations and their sub-locations (rooms, floors, areas)
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreateSiteModal}>
            Add Primary Location
          </Button>
        </Group>

        <Paper p="md" withBorder>
          <TextInput
            placeholder="Search locations..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </Paper>

        {filteredSites.length === 0 ? (
          <Paper p="xl" withBorder>
            <Center>
              <Stack align="center" gap="md">
                <IconBuilding size={48} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed">No locations found. Add your first location to get started.</Text>
                <Button leftSection={<IconPlus size={16} />} onClick={openCreateSiteModal}>
                  Add Primary Location
                </Button>
              </Stack>
            </Center>
          </Paper>
        ) : (
          <Accordion variant="separated" radius="md">
            {filteredSites.map((site) => {
              const subLocs = getSubLocations(site.id);
              return (
                <Accordion.Item key={site.id} value={site.id}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap" pr="md">
                      <Group gap="sm" wrap="nowrap">
                        <IconBuilding size={20} color="var(--mantine-color-blue-6)" />
                        <Box>
                          <Text fw={500}>{site.name}</Text>
                          {formatSiteAddress(site) && (
                            <Text size="xs" c="dimmed">
                              {formatSiteAddress(site)}
                            </Text>
                          )}
                        </Box>
                      </Group>
                      <Badge size="sm" variant="light" color="gray">
                        {subLocs.length} sub-location{subLocs.length !== 1 ? 's' : ''}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Text size="sm" fw={500} c="dimmed">
                          Sub-Locations
                        </Text>
                        <Group gap="xs">
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => openCreateSubLocationModal(site.id)}
                          >
                            Add Sub-Location
                          </Button>
                          <Menu shadow="md" width={200} position="bottom-end">
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray">
                                <IconDotsVertical size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconEdit size={14} />}
                                onClick={() => openEditSiteModal(site)}
                              >
                                Edit Location
                              </Menu.Item>
                              <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => confirmDeleteSite(site)}
                              >
                                Delete Location
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Group>

                      {subLocs.length === 0 ? (
                        <Paper p="md" bg="gray.0" radius="md">
                          <Center>
                            <Stack align="center" gap="xs">
                              <IconDoor size={24} color="var(--mantine-color-gray-5)" />
                              <Text size="sm" c="dimmed">
                                No sub-locations yet
                              </Text>
                            </Stack>
                          </Center>
                        </Paper>
                      ) : (
                        <Table>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Name</Table.Th>
                              <Table.Th>Type</Table.Th>
                              <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {subLocs.map((loc) => (
                              <Table.Tr key={loc.id}>
                                <Table.Td>
                                  <Group gap="xs">
                                    <IconDoor size={16} color="var(--mantine-color-gray-6)" />
                                    <Text size="sm">{loc.name}</Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Badge size="sm" variant="light" color="gray">
                                    {loc.type}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs">
                                    <ActionIcon
                                      variant="subtle"
                                      color="gray"
                                      size="sm"
                                      onClick={() => openEditSubLocationModal(loc)}
                                    >
                                      <IconEdit size={14} />
                                    </ActionIcon>
                                    <ActionIcon
                                      variant="subtle"
                                      color="red"
                                      size="sm"
                                      onClick={() => confirmDeleteSubLocation(loc)}
                                    >
                                      <IconTrash size={14} />
                                    </ActionIcon>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}

        <Text size="sm" c="dimmed">
          {filteredSites.length} primary location{filteredSites.length !== 1 ? 's' : ''},{' '}
          {locations.length} total sub-location{locations.length !== 1 ? 's' : ''}
        </Text>
      </Stack>

      {/* Site Modal */}
      <Modal
        opened={siteModalOpened}
        onClose={closeSiteModal}
        title={editingSite ? 'Edit Primary Location' : 'Create Primary Location'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Location Name"
            placeholder="e.g., Maryland Main Office"
            required
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            autoFocus
          />
          <TextInput
            label="Street Address"
            placeholder="e.g., 123 Main St"
            value={siteAddress}
            onChange={(e) => setSiteAddress(e.target.value)}
          />
          <Group grow>
            <TextInput
              label="City"
              placeholder="e.g., Baltimore"
              value={siteCity}
              onChange={(e) => setSiteCity(e.target.value)}
            />
            <TextInput
              label="State"
              placeholder="e.g., MD"
              value={siteState}
              onChange={(e) => setSiteState(e.target.value)}
            />
            <TextInput
              label="Postal Code"
              placeholder="e.g., 21201"
              value={sitePostalCode}
              onChange={(e) => setSitePostalCode(e.target.value)}
            />
          </Group>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeSiteModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSiteSubmit}
              loading={createSite.isPending || updateSite.isPending}
              disabled={!siteName.trim()}
            >
              {editingSite ? 'Update Location' : 'Create Location'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Sub-Location Modal */}
      <Modal
        opened={subLocationModalOpened}
        onClose={closeSubLocationModal}
        title={editingLocation ? 'Edit Sub-Location' : 'Create Sub-Location'}
        size="md"
      >
        <Stack gap="md">
          {selectedSiteForModal && (
            <Paper p="sm" withBorder bg="gray.0">
              <Group gap="xs">
                <IconMapPin size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm">
                  {editingLocation ? 'Location:' : 'Adding to:'}{' '}
                  <strong>{selectedSiteForModal.name}</strong>
                </Text>
              </Group>
            </Paper>
          )}
          <TextInput
            label="Sub-Location Name"
            placeholder="e.g., Room 104, Floor 2, Server Room"
            required
            value={subLocationName}
            onChange={(e) => setSubLocationName(e.target.value)}
            autoFocus
          />
          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={closeSubLocationModal}>
              Cancel
            </Button>
            <Group gap="sm">
              {!editingLocation && (
                <Button
                  variant="light"
                  onClick={() => handleSubLocationSubmit(true)}
                  loading={createLocation.isPending}
                  disabled={!subLocationName.trim()}
                >
                  Create & Add Another
                </Button>
              )}
              <Button
                onClick={() => handleSubLocationSubmit(false)}
                loading={createLocation.isPending || updateLocation.isPending}
                disabled={!subLocationName.trim()}
              >
                {editingLocation ? 'Update' : 'Create'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
